import { useMemo } from 'react';

import { GroupMembership } from '@aslanpay/portal-common-lib';

import useFetchIndividuals from 'app/hooks/useFetchIndividuals';
import useGroups from 'app/hooks/useGroups';

/**
 * The people a campaign's prides are decided by, named.
 * <p>
 * The company's memberships answer most of it in one read and carry the name, the job title and whether the
 * person holds the pride's pot. Anyone appointed from outside every pride is looked up by id, which is the
 * only case memberships cannot name.
 */
function useManagerDirectory(companyId: string, appointedIds: string[]) {
  const { data: memberships } = useGroups.useFetchCompanyGroupMemberships(companyId);

  const { data: appointed } = useFetchIndividuals({
    companyId: [companyId],
    ids: appointedIds,
    page: 0,
    size: 50,
    enabled: appointedIds.length > 0,
  });

  const membershipList = useMemo<GroupMembership[]>(() => memberships?.list ?? [], [memberships]);

  const nameOf = useMemo(() => {
    const byId = new Map<string, string>();

    membershipList.forEach((member) => byId.set(member.individualId, member.individualName));
    (appointed?.list ?? []).forEach((person) =>
      byId.set(person.id, `${person.firstName} ${person.lastName}`)
    );

    return (id: string) => byId.get(id) ?? 'Unknown employee';
  }, [membershipList, appointed]);

  return { memberships: membershipList, nameOf };
}

export default useManagerDirectory;
